import { forwardRef, useState, type InputHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Field } from '@/components/ui/Field';
import {
  describedByIds,
  FIELD_TRAILING_ICON,
  fieldControlClass,
  fieldIconData,
} from '@/components/ui/fieldClasses';

type PasswordInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label?: string;
  error?: string;
  hint?: string;
};

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput(
    { className, label, error, hint, id, required, 'aria-describedby': describedBy, ...props },
    ref,
  ) {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);
    const inputId = id ?? props.name;
    const hintId = inputId ? `${inputId}-hint` : undefined;
    const errorId = inputId ? `${inputId}-error` : undefined;
    const toggleLabel = visible ? t('auth.password.hide') : t('auth.password.show');

    return (
      <Field
        label={label}
        htmlFor={inputId}
        required={required}
        hint={hint}
        error={error}
        hintId={hintId}
        errorId={errorId}
      >
        <div className="relative">
          <input
            id={inputId}
            ref={ref}
            type={visible ? 'text' : 'password'}
            required={required}
            className={fieldControlClass({
              error: Boolean(error),
              className,
            })}
            aria-invalid={Boolean(error) || undefined}
            aria-describedby={
              inputId
                ? describedByIds({
                    id: inputId,
                    error: Boolean(error),
                    hint: Boolean(hint),
                    extra: describedBy,
                  })
                : describedBy
            }
            {...props}
            {...fieldIconData({ trailing: true })}
          />
          <button
            type="button"
            tabIndex={0}
            onClick={() => setVisible((current) => !current)}
            aria-label={toggleLabel}
            aria-pressed={visible}
            aria-controls={inputId}
            className={cn(FIELD_TRAILING_ICON, 'hover:text-ds-text')}
          >
            {visible ? (
              <EyeOff className="h-4 w-4" aria-hidden="true" />
            ) : (
              <Eye className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        </div>
      </Field>
    );
  },
);
